import { Component, type ReactNode } from 'react';

const RELOAD_KEY = '__spa_reload_count';
const MAX_RELOADS = 2;

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /error loading dynamically imported module/i,
  /chunkloaderror/i,
];

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(msg));
}

function readReloadCount(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY) || '0') || 0;
  } catch {
    return 0;
  }
}

function bumpReloadCount(): number {
  try {
    const next = readReloadCount() + 1;
    sessionStorage.setItem(RELOAD_KEY, String(next));
    return next;
  } catch {
    return MAX_RELOADS + 1; // storage disabled → skip reload, show fallback
  }
}

function clearReloadCount(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch { /* noop */ }
}

function forceReload(): void {
  // location.reload() alone can serve from disk-cache on some browsers
  // (Mobile Safari, older Chrome). Append a cache-bust query param to force
  // a fresh network fetch of the index.html → main bundle → all chunks.
  const url = new URL(window.location.href);
  url.searchParams.set('_r', String(Date.now()));
  window.location.replace(url.toString());
}

interface ChunkLoadGuardProps {
  children: ReactNode;
}

interface ChunkLoadGuardState {
  fatal: boolean;
}

export class ChunkLoadGuard extends Component<ChunkLoadGuardProps, ChunkLoadGuardState> {
  state: ChunkLoadGuardState = { fatal: false };

  static getDerivedStateFromError(error: unknown): ChunkLoadGuardState | null {
    if (!isChunkLoadError(error)) return null; // rethrow up to outer boundary
    if (readReloadCount() >= MAX_RELOADS) {
      return { fatal: true };
    }
    return null;
  }

  componentDidCatch(error: unknown): void {
    if (!isChunkLoadError(error)) {
      // Re-throw so outer errorElement / AppErrorFallback handles it.
      throw error;
    }
    if (readReloadCount() < MAX_RELOADS) {
      bumpReloadCount();
      // Defer reload to next tick so React finishes its commit cycle.
      setTimeout(forceReload, 0);
    }
  }

  componentDidMount(): void {
    // Successful render after a previous reload → clear the counter so a
    // future, unrelated chunk error doesn't immediately hit the fatal cap.
    if (readReloadCount() > 0) clearReloadCount();
  }

  render() {
    if (this.state.fatal) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-bg-base text-text-main">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-xl font-semibold">Neue Version verfügbar</h1>
            <p className="text-sm text-text-muted">
              Diese App wurde im Hintergrund aktualisiert. Bitte den Browser-Cache leeren
              (Cmd+Shift+R bzw. Strg+Shift+R) und erneut laden.
            </p>
            <button
              onClick={() => { clearReloadCount(); forceReload(); }}
              className="px-4 py-2 bg-accent text-bg-base rounded-lg text-sm font-medium hover:opacity-90"
            >
              Erneut laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children as ReactNode;
  }
}

export default ChunkLoadGuard;
